import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Loader2 } from 'lucide-react'

const MAX_SIZE_MB = 20
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

interface UploadZoneProps {
  onUpload: (file: File) => void
  onError?: (message: string) => void
  isUploading?: boolean
}

export default function UploadZone({ onUpload, onError, isUploading }: UploadZoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) onUpload(acceptedFiles[0])
    },
    [onUpload]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      const rejection = rejections[0]
      const isTooLarge = rejection?.errors.some((e) => e.code === 'file-too-large')
      const isWrongType = rejection?.errors.some((e) => e.code === 'file-invalid-type')
      if (isTooLarge) {
        onError?.(`El archivo supera el límite de ${MAX_SIZE_MB} MB. Usa un archivo más pequeño.`)
      } else if (isWrongType) {
        onError?.('Tipo de archivo no permitido. Solo se aceptan PDF, DOCX y TXT.')
      } else {
        onError?.('No se pudo cargar el archivo. Verifica que sea PDF, DOCX o TXT y menor a ${MAX_SIZE_MB} MB.')
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: MAX_SIZE_BYTES,
    disabled: isUploading,
  })

  return (
    <div
      {...getRootProps()}
      className="rounded-2xl p-10 text-center transition-all duration-200"
      style={{
        border: isDragActive
          ? '2px dashed #4F46E5'
          : '2px dashed rgba(255,255,255,0.12)',
        background: isDragActive
          ? 'rgba(79,70,229,0.08)'
          : 'rgba(255,255,255,0.02)',
        cursor: isUploading ? 'not-allowed' : 'pointer',
        opacity: isUploading ? 0.6 : 1,
      }}
    >
      <input {...getInputProps()} />

      <div className="flex flex-col items-center gap-4">
        {isUploading ? (
          <>
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: 'rgba(79,70,229,0.15)' }}
            >
              <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#4F46E5' }} />
            </div>
            <div>
              <p className="font-semibold" style={{ color: '#E2E8F0' }}>Subiendo archivo...</p>
              <p className="text-sm mt-1" style={{ color: '#64748B' }}>Un momento por favor</p>
            </div>
          </>
        ) : (
          <>
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: isDragActive ? 'rgba(79,70,229,0.2)' : 'rgba(255,255,255,0.05)',
              }}
            >
              {isDragActive
                ? <Upload className="w-7 h-7" style={{ color: '#818CF8' }} />
                : <FileText className="w-7 h-7" style={{ color: '#475569' }} />
              }
            </div>
            <div>
              {isDragActive ? (
                <p className="font-semibold" style={{ color: '#818CF8' }}>
                  Suelta el archivo aquí
                </p>
              ) : (
                <>
                  <p className="font-semibold" style={{ color: '#E2E8F0' }}>
                    Arrastra tu archivo o{' '}
                    <span style={{ color: '#818CF8' }}>haz clic para seleccionar</span>
                  </p>
                  <p className="text-sm mt-1.5" style={{ color: '#475569' }}>
                    PDF, DOCX o TXT · Máximo {MAX_SIZE_MB} MB
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
